import { supabase } from '../lib/supabase';
import { getSignedUrl as getInboxSignedUrl } from './invoiceInboxService';

/**
 * Documents d'une facture sous-traitant — SOURCE UNIQUE.
 *
 * Le badge de la ligne et la modale Documents listaient le Storage chacun de
 * leur cote, avec deux implementations divergentes. Ils affichaient donc deux
 * reponses differentes pour le meme fichier. Un seul chemin de code ici, un
 * seul resultat possible.
 *
 * Cascade a TROIS etapes, reprise de facture-approval/index.jsx:78-90, qui est
 * le seul endroit ou elle fonctionnait :
 *   1. bucket sub-invoices        {projectId}/{invoiceId}
 *   2. bucket contracts-documents sub_invoices/{invoiceId}
 *   3. invoice_inbox par linked_invoice_id -> file_path signe sur invoice-inbox
 *
 * L'etape 3 manquait aux deux appelants : les pieces televersees par la boite
 * de reception vivent dans un troisieme bucket que personne ne consultait.
 *
 * CHAQUE appel a son propre try/catch. Un echec sur une etape passe a la
 * suivante — il n'avale jamais les deux autres. C'est exactement le defaut qui
 * vidait la modale : une exception a l'etape 1 faisait tomber tout le reste.
 */

const PLACEHOLDER = '.emptyFolderPlaceholder';

const realFiles = (list) =>
  (list || [])?.filter((f) => f?.name && f?.name !== PLACEHOLDER);

/**
 * @returns {Promise<Array<{ name, bucket, path, source, id? }>>}
 *   source : 'sub-invoices' | 'contracts-documents' | 'invoice-inbox'
 */
export async function listInvoiceDocuments(invoiceId, projectId = null) {
  if (!invoiceId) return [];

  // Le projet peut ne pas etre fourni (la modale ne le recoit pas en prop).
  let resolvedProjectId = projectId;
  if (!resolvedProjectId) {
    try {
      const { data, error } = await supabase
        ?.from('sub_invoices')
        ?.select('project_id')
        ?.eq('id', invoiceId)
        ?.maybeSingle();
      if (error) throw error;
      resolvedProjectId = data?.project_id || null;
    } catch (err) {
      console.error('[invoiceDocuments] project lookup failed:', err?.message);
    }
  }

  // ── 1. Bucket sub-invoices ──
  if (resolvedProjectId) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('sub-invoices')
        ?.list(`${resolvedProjectId}/${invoiceId}`, {
          limit: 100,
          sortBy: { column: 'name', order: 'asc' },
        });
      if (error) throw error;
      const files = realFiles(data);
      if (files?.length > 0) {
        return files?.map((f) => ({
          name: f?.name,
          bucket: 'sub-invoices',
          path: `${resolvedProjectId}/${invoiceId}/${f?.name}`,
          source: 'sub-invoices',
        }));
      }
    } catch (err) {
      console.error('[invoiceDocuments] sub-invoices list failed:', err?.message);
    }
  }

  // ── 2. Bucket contracts-documents (heritage) ──
  try {
    const { data, error } = await supabase?.storage
      ?.from('contracts-documents')
      ?.list(`sub_invoices/${invoiceId}`, {
        limit: 100,
        sortBy: { column: 'name', order: 'asc' },
      });
    if (error) throw error;
    const files = realFiles(data);
    if (files?.length > 0) {
      return files?.map((f) => ({
        name: f?.name,
        bucket: 'contracts-documents',
        path: `sub_invoices/${invoiceId}/${f?.name}`,
        source: 'contracts-documents',
      }));
    }
  } catch (err) {
    console.error('[invoiceDocuments] contracts-documents list failed:', err?.message);
  }

  // ── 3. Boite de reception ──
  // La piece d'origine, quand la facture a ete creee depuis une facture recue.
  try {
    const { data, error } = await supabase
      ?.from('invoice_inbox')
      ?.select('id, file_path, file_name')
      ?.eq('linked_invoice_id', invoiceId)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || [])
      ?.filter((r) => r?.file_path)
      ?.map((r) => ({
        id: r?.id,
        name: r?.file_name || r?.file_path?.split('/')?.pop(),
        bucket: 'invoice-inbox',
        path: r?.file_path,
        source: 'invoice-inbox',
      }));
  } catch (err) {
    console.error('[invoiceDocuments] invoice_inbox lookup failed:', err?.message);
    return [];
  }
}

/** URL signee d'un document rendu par listInvoiceDocuments. */
export async function getInvoiceDocumentUrl(doc, expiresIn = 3600) {
  if (!doc?.path) return null;
  // getSignedUrl vise deja le bucket invoice-inbox : on le reutilise tel quel.
  if (doc?.bucket === 'invoice-inbox') return getInboxSignedUrl(doc?.path, expiresIn);
  const { data, error } = await supabase?.storage
    ?.from(doc?.bucket)
    ?.createSignedUrl(doc?.path, expiresIn);
  if (error) throw new Error(error.message);
  return data?.signedUrl || null;
}

export default { listInvoiceDocuments, getInvoiceDocumentUrl };
